<template>
  <modal
    :name="modalName"
    width="94%"
    height="auto"
    :max-width="1200"
    :adaptive="true"
    :scrollable="true"
    @opened="focusClose"
    @before-close="beforeClose"
  >
    <div
      class="automation-anchor-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="automation-anchor-title"
      @keyup.esc.stop.prevent="$emit('close')"
    >
      <AppModalHeader
        ref="header"
        design="new"
        title="Anchor communication"
        :eyebrow="`Communication #${communicationId}`"
        title-id="automation-anchor-title"
        @close="$emit('close')"
      />
      <main class="automation-anchor-body">
        <ResourceState :resource="detail" label="communication" @reload="loadDetail">
          <template #default="{ data: communication }">
            <div class="automation-anchor-layout">
              <div class="automation-anchor-details">
                <DetailCard title="Communication context">
                  <Fields :fields="contextFields(communication)" />
                </DetailCard>

                <DetailCard title="Technical details">
                  <Fields :fields="technicalFields(communication)" />
                </DetailCard>
              </div>

              <div class="automation-anchor-message-column">
                <DetailCard title="Message" class="automation-anchor-message-card">
                  <template #actions>
                    <AppStatusBadge
                      design="new"
                      compact
                      :tone="statusTone(communication.delivery_status)"
                      :label="display(communication.delivery_status)"
                    />
                  </template>
                  <strong v-if="communication.subject" class="automation-anchor-subject">
                    {{ communication.subject }}
                  </strong>
                  <pre v-if="communication.report" class="automation-anchor-message">{{ communication.report }}</pre>
                  <p v-else class="automation-anchor-muted">No message text was recorded.</p>
                </DetailCard>
              </div>
            </div>
          </template>
        </ResourceState>
      </main>
    </div>
  </modal>
</template>

<script>
import { getLeadFlowCommunication } from '@/http'
import resources, { resource } from './resources'
import { dateTime, display, statusTone } from './model'
import ResourceState from './ResourceState.vue'
import Fields from './Fields.vue'
import DetailCard from './DetailCard.vue'

export default {
  name: 'FollowUpAnchorCommunicationModal',
  components: { ResourceState, Fields, DetailCard },
  mixins: [resources],
  props: {
    leadId: { type: Number, required: true },
    communicationId: { type: Number, required: true }
  },
  data: () => ({
    detail: resource()
  }),
  computed: {
    modalName() {
      return `automation-anchor-${this.leadId}-${this.communicationId}`
    }
  },
  mounted() {
    this.$modal.show(this.modalName)
    this.loadDetail()
  },
  methods: {
    display,
    statusTone,
    focusClose() {
      this.$refs.header.focusClose()
    },
    beforeClose(event) {
      event.cancel()
      this.$emit('close')
    },
    loadDetail() {
      return this.loadResource(this.detail, () => (
        getLeadFlowCommunication(this.leadId, this.communicationId)
      ))
    },
    contextFields(communication) {
      return [
        ['Date', dateTime(communication.date)],
        ['Channel', (communication.methods || []).map(item => item.name).filter(Boolean).join(' / ')],
        ['Source', communication.source],
        ['Direction', communication.direction],
        ['Delivery status', communication.delivery_status],
        ['Lead stage', communication.stage]
      ]
    },
    technicalFields(communication) {
      const provider = communication.provider_conversation
      return [
        ['Communication ID', communication.id],
        ['External message ID', communication.external_message_id],
        ['Provider conversation', provider && `${provider.type} · ${provider.id}`],
        ['Linked flow events', (communication.linked_flow_events || []).join(', ')],
        ['Counts as legacy contact', communication.counts_as_legacy_contact ? 'Yes' : 'No']
      ]
    }
  }
}
</script>

<style scoped lang="scss">
.automation-anchor-dialog {
  overflow: hidden;
  border-radius: $ui-overlay-radius;
  background: $ui-surface-soft;
  color: $ui-text;
  font: #{$ui-font-size-body}/ 1.45 $ui-font-body;
}
.automation-anchor-body {
  padding: $ui-space-20;
}
.automation-anchor-layout {
  display: flex !important;
  align-items: stretch;
  gap: $ui-space-20;
}
.automation-anchor-details {
  display: flex !important;
  flex: 0 0 calc(44% - 10px);
  flex-direction: column;
  min-width: 0;
  gap: $ui-space-20;
}
.automation-anchor-message-column {
  display: flex;
  min-width: 0;
  flex: 1 1 0;
}
.automation-anchor-message-card {
  min-width: 0;
  flex: 1 1 auto;
}
.automation-anchor-subject {
  display: block;
  margin-bottom: $ui-space-12;
  font-family: $ui-font-heading;
}
.automation-anchor-message {
  margin: 0;
  padding: $ui-space-16;
  border: 1px solid $ui-line;
  border-radius: $ui-panel-radius;
  background: $ui-surface-soft;
  font: inherit;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.automation-anchor-muted {
  margin: 0;
  color: $ui-muted;
}
@media (max-width: 600px) {
  .automation-anchor-body {
    padding: $ui-space-12;
  }
}
</style>
